import { useCallback, useEffect, useMemo, useState } from 'react';

import { PROGRAMS } from '../../constants/programs';
import { PROGRAM_CODE_MAP, isSelectionActive } from '../../utils/dataNormalizer';
import { getEnrollmentByProgram } from '../../hooks/useApi';
import { ALL_PROGRAMS_OPTION } from './constants';
import { resolveProgramNameKey } from './utils';
import { logger } from '../../utils/logger';
import { mergeEnrollmentRow, normalizeEnrollmentBac } from '../../utils/enrollmentRowMerge';
import { resolveEnrollmentLifecycleStatus } from '../../utils/enrollmentEvidence';
import { isSelectionCoverageEligible } from '../../utils/enrollmentCoverage';

export const enrollmentRowMatchesActiveFilter = (row, activeFilter) => {
  if (!activeFilter) return true;
  if (!row) return false;
  if (activeFilter.type === 'status') {
    return row.programName === activeFilter.programName &&
      (resolveEnrollmentLifecycleStatus(row) || 'LIFECYCLE_UNAVAILABLE') === activeFilter.value;
  }
  if (activeFilter.type === 'selection') {
    if (row.programName !== activeFilter.programName) return false;
    if (!isSelectionCoverageEligible(row)) return false;
    const hasSelections = Array.isArray(row.productSelections) && row.productSelections.some(isSelectionActive);
    if (activeFilter.value === 'With Selections') return hasSelections;
    if (activeFilter.value === 'Without Selections') return !hasSelections;
  }
  return true;
};

export const useEnrollmentModel = ({
  queryClient,
  hasSearched,
  searchType,
  searchValue,
  activeFilter,
  allProgramsData,
  programData,
  isProgramFetching,
  setPaginationModel,
}) => {
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Prefetch program data when the dropdown changes to reduce wait on switch.
  useEffect(() => {
    if (searchType !== 'program') return;
    if (!searchValue || searchValue === ALL_PROGRAMS_OPTION) return;
    queryClient.prefetchQuery({
      queryKey: ['enrollmentData', { type: 'program', value: searchValue }],
      queryFn: () => getEnrollmentByProgram(searchValue),
      staleTime: 5 * 60 * 1000,
    });
  }, [queryClient, searchType, searchValue]);

  // Populate the visible table using cached data when possible to avoid flicker between searches.
  const runLiveSearch = useCallback(() => {
    if (hasSearched) return;
    if (!searchValue) {
      setDisplayedData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    if (searchType === 'program') {
      if (searchValue === ALL_PROGRAMS_OPTION) {
        const fromCache = queryClient.getQueryData(['allEnrollmentData']) || allProgramsData || [];
        const combined = Array.isArray(fromCache)
          ? fromCache.flatMap((entry) => entry.data || [])
          : [];
        setDisplayedData(combined);
      } else {
        setDisplayedData(programData || []);
      }
    } else if (searchType === 'bac') {
      const combinedResults = [];
      PROGRAMS.forEach((programName) => {
        const queryKey = ['enrollmentData', { type: 'program', value: programName }];
        const cachedData = queryClient.getQueryData(queryKey);
        if (cachedData) {
          const matches = cachedData.filter((report) => report.bac && report.bac.toString() === searchValue);
          if (matches.length > 0) {
            const enrollmentsWithProgram = matches.map((entry) => ({
              ...entry,
              programName,
              programCode: entry.programCode || PROGRAM_CODE_MAP[programName] || entry.program,
            }));
            combinedResults.push(...enrollmentsWithProgram);
          }
        }
      });
      setDisplayedData(combinedResults);
    }

    setIsLoading(false);
  }, [allProgramsData, hasSearched, programData, queryClient, searchType, searchValue, setPaginationModel]);

  const filteredData = useMemo(() => {
    if (!activeFilter) return displayedData;

    const filtered = displayedData.filter((row) => enrollmentRowMatchesActiveFilter(row, activeFilter));

    return filtered;
  }, [displayedData, activeFilter]);

  const syncDisplayedDataForProgram = useCallback(
    (programName) => {
      if (!programName) return;
      const programKey = ['enrollmentData', { type: 'program', value: programName }];
      const cachedRows = queryClient.getQueryData(programKey);
      if (Array.isArray(cachedRows)) {
        setDisplayedData(cachedRows);
      }
    },
    [queryClient]
  );

  // Normalize mutations (e.g., refreshed selections) back into the cached datasets.
  const handleRowUpdated = useCallback(
        (updatedRow) => {
          if (!updatedRow) return;
          const updatedProgramName = resolveProgramNameKey(updatedRow);
          const programKey = updatedRow.programCode || updatedRow.program || updatedProgramName || updatedRow.programName;
          const cacheProgramName = resolveProgramNameKey(updatedRow);
          const updatedBac = normalizeEnrollmentBac(updatedRow);
          setDisplayedData((prev) => {
            let matched = false;
            const next = prev.map((row) => {
              if (!row) return row;
              const rowProgramName = resolveProgramNameKey(row);
              const rowKey = row.programCode || row.program || rowProgramName || row.programName;
              const sameProgram =
                (updatedProgramName && rowProgramName && updatedProgramName === rowProgramName) || rowKey === programKey;
              if (normalizeEnrollmentBac(row) === updatedBac && sameProgram) {
                matched = true;
                return mergeEnrollmentRow(row, updatedRow);
              }
              return row;
            });
            return matched ? next : prev;
      });

      if (cacheProgramName) {
        const programQueryKey = ['enrollmentData', { type: 'program', value: cacheProgramName }];
        queryClient.setQueryData(programQueryKey, (existing) => {
          if (!Array.isArray(existing)) return existing;
          let changed = false;
          const next = existing.map((row) => {
            if (!row || normalizeEnrollmentBac(row) !== updatedBac) return row;
            const rowProgramName = resolveProgramNameKey(row);
            if (rowProgramName !== cacheProgramName) return row;
            changed = true;
            return mergeEnrollmentRow(row, updatedRow);
          });
          return changed ? next : existing;
        });

        queryClient.setQueryData(['allEnrollmentData'], (existing) => {
          if (!Array.isArray(existing)) return existing;
          let changed = false;
          const next = existing.map((entry) => {
            if (!entry || entry.programName !== cacheProgramName || !Array.isArray(entry.data)) return entry;
            let localChanged = false;
            const updatedData = entry.data.map((row) => {
              if (!row || normalizeEnrollmentBac(row) !== updatedBac) return row;
              localChanged = true;
              return mergeEnrollmentRow(row, updatedRow);
            });
            if (!localChanged) return entry;
            changed = true;
            return { ...entry, data: updatedData };
          });
          return changed ? next : existing;
        });
      }

      logger.debug('Row updated', { bac: updatedRow.bac, program: programKey });
    },
    [queryClient]
  );

  const isFetching = isLoading || isProgramFetching;

  return {
    displayedData,
    setDisplayedData,
    filteredData,
    runLiveSearch,
    handleRowUpdated,
    syncDisplayedDataForProgram,
    isFetching,
    isLoading,
    setIsLoading,
  };
};
